import { wpBaseUrl } from '../config';

import fetch from '@dojo/framework/shim/fetch';
import * as parse from 'parse-link-header';

export interface WpCategory {
	id: number;
	count: number;
	description: string;
	link: string;
	taxonomy: string;
	parent: number;
	yoast_head: string;
	slug: string;
	name: string;
}

export interface WpSeries extends WpCategory {}

export interface WpBlogPreview {
	jetpack_featured_media_url: string;
	slug: string;
	id: string;
	date: string;
	categories: number[];
	title: {
		rendered: string;
	};
	excerpt: {
		rendered: string;
	};
	_embedded: {
		author: {
			name: string;
		}[];
		'wp:featuredmedia': {
			media_details?: {
				sizes?: {
					large?: {
						source_url: string;
					};
					medium?: {
						source_url: string;
					};
				};
			};
		}[];
	};
}

interface WpBlog extends WpBlogPreview {
	content: {
		rendered: string;
	};
	series: number[];
	link: string;
	yoast_head: string;
}

export interface WpSearchResult {
	_embedded: {
		self: [WpBlogPreview];
	};
}

export const getCategories = async (): Promise<WpCategory[]> => {
	let url = `${wpBaseUrl}/wp-json/wp/v2/categories?per_page=100`;
	const categories: any[] = [];
	while (url) {
		const response = await fetch(url);
		url = '';
		const link = response.headers.get('link');
		if (link) {
			const parsedLinks = parse(link);
			if (parsedLinks && parsedLinks.next) {
				url = parsedLinks.next.url;
			}
		}
		const json: WpCategory[] = await response.json();
		categories.push(...json);
	}
	return categories;
};

export const getSeries = async (): Promise<WpSeries[]> => {
	let url = `${wpBaseUrl}/wp-json/wp/v2/series?per_page=100`;
	const series: any[] = [];
	while (url) {
		const response = await fetch(url);
		url = '';
		const link = response.headers.get('link');
		if (link) {
			const parsedLinks = parse(link);
			if (parsedLinks && parsedLinks.next) {
				url = parsedLinks.next.url;
			}
		}
		const json: WpSeries[] = await response.json();
		series.push(...json);
	}
	return series;
};

export const getBlogPreviews = async (
	size: number,
	page: number,
	options?: { category?: number; series?: number }
): Promise<{ data: WpBlogPreview[]; total: number; pages: number }> => {
	let url = `${wpBaseUrl}/wp-json/wp/v2/posts?per_page=${size}&page=${page}&_embed=wp:featuredmedia,author`;

	if (options?.category) {
		url = `${url}&categories=${options.category}`;
	}
	if (options?.series) {
		url = `${url}&series=${options.series}`;
	}

	const response = await fetch(url);
	const json: WpBlogPreview[] = await response.json();
	return {
		data: json,
		total: parseInt(String(response.headers.get('x-wp-total'))),
		pages: parseInt(String(response.headers.get('x-wp-totalpages')))
	};
};

export const getBlog = async (slug: string): Promise<WpBlog> => {
	const response = await fetch(
		`${wpBaseUrl}/wp-json/wp/v2/posts?slug=${slug}&_embed=wp:featuredmedia,author`
	);
	const [wpBlog]: WpBlog[] = await response.json();
	return wpBlog;
};

export const getSearchResults = async (searchTerm: string): Promise<WpSearchResult[]> => {
	const response = await fetch(
		`${wpBaseUrl}/wp-json/wp/v2/search?search=${searchTerm}&per_page=30&_embed`
	);
	const json: WpSearchResult[] = await response.json();
	return json;
};
